/** A consumer applies whole replacements; transport reconnect always starts with a snapshot. */
export class ConversationView {
    #snapshot;
    #available = false;
    #items = new Map();
    #olderIds = [];
    #pendingPage;
    #before = null;
    #connection = 0;
    get connection() {
        return this.#connection;
    }
    reconnect() {
        this.#connection++;
        this.cancelPage();
    }
    get snapshot() {
        return this.#snapshot;
    }
    get available() {
        return this.#available;
    }
    get items() {
        return [...new Set([...this.#olderIds, ...(this.#snapshot?.window.itemIds ?? [])])].flatMap((id) => {
            const item = this.#items.get(id);
            return item ? [item] : [];
        });
    }
    get before() {
        return this.#before;
    }
    apply(frame) {
        if (frame.type === "conversation_unavailable") {
            this.#available = false;
            return;
        }
        if (frame.type === "conversation_snapshot") {
            const unchanged = this.#snapshot?.epoch === frame.epoch && this.#snapshot.revision === frame.revision;
            this.#snapshot = frame;
            this.#available = true;
            if (unchanged)
                return;
            this.#items = new Map(frame.items.map((item) => [item.id, item]));
            this.#olderIds = [];
            this.#pendingPage = undefined;
            this.#before = frame.window.before;
            return;
        }
        const prior = this.#snapshot;
        if (!prior || frame.epoch !== prior.epoch)
            throw new Error("Conversation requires a new snapshot");
        if (frame.revision <= prior.revision)
            return;
        if (frame.revision !== prior.revision + 1)
            throw new Error("Conversation revision gap");
        const retained = new Set([...this.#olderIds, ...frame.window.itemIds]);
        for (const item of frame.items) {
            if (retained.has(item.id))
                this.#items.set(item.id, item);
            if (this.#pendingPage)
                this.#pendingPage.replacements.set(item.id, { item, revision: frame.revision });
        }
        if (this.#pendingPage && this.#pendingPage.replacements.size > 200) {
            this.#pendingPage = undefined;
        }
        for (const id of this.#items.keys())
            if (!retained.has(id))
                this.#items.delete(id);
        this.#snapshot = {
            type: "conversation_snapshot",
            sessionId: prior.sessionId,
            epoch: frame.epoch,
            revision: frame.revision,
            window: frame.window,
            items: frame.window.itemIds.flatMap((id) => this.#items.get(id) ?? []),
            runs: frame.runs,
            approvals: frame.approvals,
        };
        if (!this.#olderIds.length)
            this.#before = frame.window.before;
        this.#available = true;
    }
    beginPage() {
        if (!this.#snapshot || !this.#before)
            throw new Error("No older history is available");
        if (this.#pendingPage)
            throw new Error("History is already loading");
        this.#pendingPage = {
            epoch: this.#snapshot.epoch,
            revision: this.#snapshot.revision,
            replacements: new Map(),
        };
        return { epoch: this.#snapshot.epoch, revision: this.#snapshot.revision, before: this.#before };
    }
    finishPage(page) {
        const pending = this.#pendingPage;
        this.#pendingPage = undefined;
        if (!pending ||
            !this.#snapshot ||
            page.epoch !== pending.epoch ||
            page.epoch !== this.#snapshot.epoch ||
            page.revision < pending.revision ||
            page.revision > this.#snapshot.revision)
            throw new Error("History changed while loading; retry the page");
        const live = new Set(this.#snapshot.window.itemIds);
        for (const item of page.items) {
            if (!live.has(item.id)) {
                const replacement = pending.replacements.get(item.id);
                this.#items.set(item.id, replacement && replacement.revision > page.revision ? replacement.item : item);
            }
        }
        this.#olderIds = [...new Set([...page.items.map((item) => item.id), ...this.#olderIds])];
        this.#before = page.window.before;
    }
    cancelPage() {
        this.#pendingPage = undefined;
    }
    replaceFullItem(item, epoch, revision) {
        if (this.#snapshot?.epoch !== epoch || this.#snapshot.revision !== revision)
            throw new Error("Conversation changed while loading the item; retry");
        if (this.#items.has(item.id))
            this.#items.set(item.id, item);
    }
}
